import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useEffect, FC } from 'react';
import BirthdayIcon from 'assets/images/postCategories/birthday.svg';
import { DeleteManagedUserDialogue, UnsupportedImageDialogue } from 'components/Dialogues';
import ProfilePhotoColorSelector from 'components/ProfilePhotoColorSelector/ProfilePhotoColorSelector';
import Button from 'components/UI/Button/Button';
import Dialogue from 'components/UI/Dialogue/Dialogue';
import Input from 'components/UI/Input/Input';
import SingleDatePicker from 'containers/UI/DateSelection/SingleDatePicker/SingleDatePicker';
import { formatLongFormDate } from 'shared/utility';
import useProfileForm, { ProfileFormState } from 'containers/ProfileForm/useProfileForm';
import classes from './ProfileForm.module.css';

interface ProfileFormProps {
  type?: string;
  close?: VoidFunction;
}

const ProfileForm: FC<ProfileFormProps> = ({ type, close }) => {
  const { state, callbacks, isManagedUser, editingProfile, profileCreated } = useProfileForm();
  const { handleImageUpdate } = callbacks;

  useEffect(() => {
    handleImageUpdate();
  }, [handleImageUpdate]);

  const formElements: (keyof ProfileFormState['controls'])[] = ['firstname', 'lastname', 'nickname'];
  const profileContainerClasses = [classes.ProfileContainer];
  if (type) profileContainerClasses.push(classes[type]);
  let renderedDialogue = null;
  const profileCreationRequiredMessage = null;
  const birthDateSelectorContainerClasses = [classes.BirthdateContainer];
  if (_get(state, 'controls.birthdate.validation.required') && !_get(state, 'controls.birthdate.valid') && state.attemptedFormSubmission) {
    birthDateSelectorContainerClasses.push(classes.Invalid);
  }

  const form = formElements.map((formElement) => {
    return (
      <Input
        key={formElement}
        elementType={state.controls[formElement].elementType}
        elementConfig={state.controls[formElement].elementConfig}
        value={state.controls[formElement].value}
        className={classes.Input}
        placeholder={state.controls[formElement].elementConfig.placeholder}
        invalid={!state.controls[formElement].valid}
        shouldValidate={state.attemptedFormSubmission && _pick(state, _get(state, `controls.${formElement}.validation.required`))}
        validationError={state.controls[formElement].validation.validationError}
        highlightPlaceholerOnValidationError
        touched={state.controls[formElement].touched}
        changed={(event: { target: HTMLInputElement }) => callbacks.inputChangedHandler(event, formElement)}
        guidetext={state.controls[formElement].elementConfig.guidetext}
        readOnly={state.controls[formElement].elementConfig.readOnly}
      />
    );
  });

  if (state.showCloseFormDialogue) {
    if ((isManagedUser && !profileCreated) || (profileCreated && state.formTouched)) {
      renderedDialogue = (
        <Dialogue
          subject="Are You Sure? "
          message="Are you sure you want to close this form? Your changes will not be saved."
          cancelText="Cancel"
          className="EditLog"
          dialogueType="alertDialogue"
          whiteBackground
          cancelled={callbacks.toggleProfileCloseDialogue}
          confirmationText="YES"
          confirm={callbacks.handleProfileFormClose}
        />
      );
    }
  }

  if (state.showDeleteManagedUserConfirmation === true) {
    renderedDialogue = (
      <DeleteManagedUserDialogue clicked={callbacks.handleManagedUserAccountDeletion} cancelled={callbacks.toggleDeleteManagedUserConfirmation} />
    );
  }

  let fileTypeErrorDialogue = null;
  if (state.showFileTypeError) {
    fileTypeErrorDialogue = <UnsupportedImageDialogue clicked={callbacks.hideFileTypeErrorDialogue} />;
  }

  let profileForm = (
    <div>
      <div className={classes.PhotoColorSelectionContainer}>
        <ProfilePhotoColorSelector
          showColorSelector={state.showColorSelector}
          isValid={
            state.controls.color.validation.required
              ? state.controls.color.valid || (!state.controls.color.valid && !state.attemptedFormSubmission)
              : true
          }
          profileCreated={profileCreated}
          profileThumbUrl={state.profileThumbUrl}
          handleImageUpload={callbacks.handleImageUpload}
          handleColorSelection={callbacks.handleColorSelection}
          handleColorSelectorToggle={callbacks.handleColorSelectorToggle}
          color={state.controls.color.value}
          loadingImage={state.loadingImage}
        />
      </div>
      <div className={classes.FormContainer}>{form}</div>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div className={birthDateSelectorContainerClasses.join(' ')} onClick={callbacks.toggleBirthdatePicker}>
        <img src={BirthdayIcon} alt="Birth Date" />

        {_get(state, 'controls.birthdate.value') ? (
          <p className={classes.BirthDate}>{formatLongFormDate(_get(state, 'controls.birthdate.value'))}</p>
        ) : (
          <div>
            <p className={classes.PrimaryText}>Add Birthday *</p>
            <p className={classes.SubDescription}>Your birth date is required to build a timeline.</p>
          </div>
        )}
      </div>
      {editingProfile.ProfileStatus === 'Completed' && isManagedUser ? (
        <div className={classes.DeleteManagedAccount}>
          <FontAwesomeIcon icon="trash" className={classes.DeleteIcon} onClick={callbacks.toggleDeleteManagedUserConfirmation} />
          Delete Managed Account
        </div>
      ) : null}
    </div>
  );
  const defaultDatePickerYear = new Date().getFullYear() - 25;
  const maxYear = new Date().getFullYear();
  const maxMonth = new Date().getMonth() + 1;

  if (state.showBirthdatePicker) {
    profileForm = (
      <SingleDatePicker
        totalDateRangeYears={120}
        type="ProfileForm"
        maxDate={`${maxMonth}/31/${maxYear}`}
        initialView={_get(state, 'controls.birthdate.value') ? 'date' : 'monthYear'}
        defaultYear={defaultDatePickerYear}
        date={state.controls.birthdate.value}
        handleDateSelect={callbacks.handleDateSelect}
      />
    );
  }

  return (
    <div className={profileContainerClasses.join(' ')}>
      <div className={classes.Profile}>
        <div className={classes.Header}>
          {state.showBirthdatePicker ? (
            <FontAwesomeIcon icon="chevron-left" className={classes.BackArrow} onClick={callbacks.toggleBirthdatePicker} />
          ) : (
            <>
              {!profileCreated && !isManagedUser ? null : (
                <FontAwesomeIcon icon="times" className={classes.CloseProfileForm} onClick={close || callbacks.toggleProfileCloseDialogue} />
              )}
            </>
          )}
          <h2>{state.showBirthdatePicker ? 'Birth Date' : <>{profileCreated ? 'Update Profile' : 'Create Profile'}</>}</h2>
          {state.showBirthdatePicker ? null : (
            <Button btnType="Save" clicked={callbacks.submitHandler}>
              SAVE
            </Button>
          )}
          {profileCreationRequiredMessage}
        </div>
        {profileForm}
        {renderedDialogue}
      </div>
      {fileTypeErrorDialogue}
    </div>
  );
};

export default ProfileForm;
